import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, ArrowLeft, ChevronUp, ChevronDown, Trash2, Edit, Check, X, Layers, Upload, Image as ImageIcon } from "lucide-react";
import { invalidateCategoriesCache } from "@/hooks/useCategories";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminCategoriesPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // category key
  const [editLabel, setEditLabel] = useState("");
  const [editShipping, setEditShipping] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newShipping, setNewShipping] = useState("");
  const [newUseDefaultShipping, setNewUseDefaultShipping] = useState(true);
  const [newDescription, setNewDescription] = useState("");
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");
  const [uploadingKey, setUploadingKey] = useState(null);
  const [defaultShipping, setDefaultShipping] = useState(5);
  const [editingDefault, setEditingDefault] = useState(false);
  const [defaultShippingDraft, setDefaultShippingDraft] = useState("5");
  const [editUseDefaultShipping, setEditUseDefaultShipping] = useState(false);

  const fetchDefault = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/settings/shipping`);
      setDefaultShipping(data.default_shipping_cost);
      setDefaultShippingDraft(String(data.default_shipping_cost));
    } catch (e) { console.error(e); }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/categories`);
      setCategories(data);
      invalidateCategoriesCache();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const saveDefaultShipping = async () => {
    const val = parseFloat(defaultShippingDraft);
    if (isNaN(val) || val < 0) { showError("Enter a valid number ≥ 0"); return; }
    try {
      await axios.put(`${API}/api/admin/settings/shipping`, { default_shipping_cost: val });
      setDefaultShipping(val);
      setEditingDefault(false);
      await fetchCategories();
      showFeedback(`Default shipping set to $${val.toFixed(2)}`);
    } catch (e) {
      showError(e.response?.data?.detail || "Save failed");
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { navigate("/"); return; }
    fetchCategories();
    fetchDefault();
  }, [user, authLoading, navigate, fetchCategories, fetchDefault]);

  const showFeedback = (msg) => { setFeedback(msg); setError(""); setTimeout(() => setFeedback(""), 3000); };
  const showError = (msg) => { setError(msg); setFeedback(""); };

  const startEdit = (cat) => {
    setEditing(cat.key);
    setEditLabel(cat.label);
    setEditShipping(cat.shipping_cost != null ? String(cat.shipping_cost) : "");
    setEditUseDefaultShipping(cat.shipping_cost == null);
    setEditDescription(cat.description || "");
  };
  const cancelEdit = () => { setEditing(null); setEditLabel(""); setEditShipping(""); setEditDescription(""); setEditUseDefaultShipping(false); };

  const saveEdit = async (cat) => {
    if (!editLabel.trim()) return;
    if (!window.confirm(`Save changes to "${cat.label}"? This will update the category shown to all shoppers.`)) return;
    try {
      const payload = {
        label: editLabel.trim(),
        description: editDescription,
      };
      if (editUseDefaultShipping) {
        payload.use_default_shipping = true;
      } else {
        payload.shipping_cost = parseFloat(editShipping) || 0;
      }
      await axios.put(`${API}/api/admin/categories/${cat.key}`, payload);
      cancelEdit();
      await fetchCategories();
      showFeedback(`Updated "${editLabel}"`);
    } catch (e) {
      showError(e.response?.data?.detail || "Save failed");
    }
  };

  const reorder = async (key, direction) => {
    const idx = categories.findIndex(c => c.key === key);
    const newIdx = idx + (direction === "up" ? -1 : 1);
    if (idx < 0 || newIdx < 0 || newIdx >= categories.length) return;
    const reordered = [...categories];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    setCategories(reordered.map((c, i) => ({ ...c, sort_order: i })));
    try {
      await axios.put(`${API}/api/admin/categories/reorder`, { ordered_keys: reordered.map(c => c.key) });
    } catch (e) {
      showError("Reorder failed");
      fetchCategories();
    }
  };

  const handleDelete = async (cat) => {
    let confirmMsg = `Delete category "${cat.label}"? This cannot be undone. (Only allowed if no products are in this category.)`;
    if (cat.key === "consoles" || cat.key === "bundles") {
      confirmMsg = `⚠ Heads up: the "${cat.label}" category powers the +$20 custom color upcharge. If you delete it, any future products you create in a category with key "${cat.key}" will work — but creating a different key won't trigger the color fee.\n\nDelete anyway?`;
    }
    if (!window.confirm(confirmMsg)) return;
    try {
      await axios.delete(`${API}/api/admin/categories/${cat.key}`);
      await fetchCategories();
      showFeedback(`Deleted "${cat.label}"`);
    } catch (e) {
      showError(e.response?.data?.detail || "Delete failed");
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newKey.trim() || !newLabel.trim()) return;
    if (!window.confirm(`Create new category "${newLabel}" (key: ${newKey.trim().toLowerCase()})?\n\nThe key cannot be changed later.`)) return;
    try {
      const payload = {
        key: newKey.trim().toLowerCase(),
        label: newLabel.trim(),
        description: newDescription,
      };
      if (!newUseDefaultShipping) {
        payload.shipping_cost = parseFloat(newShipping) || 0;
      }
      await axios.post(`${API}/api/admin/categories`, payload);
      setShowNew(false);
      setNewKey(""); setNewLabel(""); setNewShipping(""); setNewDescription(""); setNewUseDefaultShipping(true);
      await fetchCategories();
      showFeedback(`Created "${newLabel}"`);
    } catch (e) {
      showError(e.response?.data?.detail || "Create failed");
    }
  };

  const uploadImage = async (cat, file) => {
    if (!file) return;
    setUploadingKey(cat.key);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data: uploadResult } = await axios.post(`${API}/api/upload/image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const imageUrl = `/api/files/${uploadResult.path}`;
      await axios.put(`${API}/api/admin/categories/${cat.key}`, { image_url: imageUrl });
      await fetchCategories();
      showFeedback(`Updated image for "${cat.label}"`);
    } catch (e) {
      showError(e.response?.data?.detail || "Image upload failed");
    } finally {
      setUploadingKey(null);
    }
  };

  const clearImage = async (cat) => {
    if (!window.confirm(`Reset "${cat.label}" image to default?`)) return;
    try {
      await axios.put(`${API}/api/admin/categories/${cat.key}`, { image_url: "" });
      await fetchCategories();
      showFeedback(`Reset image for "${cat.label}"`);
    } catch (e) {
      showError(e.response?.data?.detail || "Reset failed");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050505" }}><div className="w-8 h-8 border border-[#39FF14] border-t-transparent animate-spin" /></div>;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050505" }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <button onClick={() => navigate("/admin")} data-testid="back-to-admin" className="flex items-center text-[#A1A1A1] hover:text-[#39FF14] font-mono text-xs uppercase tracking-wider mb-3">
          <ArrowLeft className="h-3 w-3 mr-2" /> Back to Admin
        </button>

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Layers className="h-5 w-5 text-[#39FF14]" strokeWidth={1.5} />
              <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14]">Admin</p>
            </div>
            <h1 data-testid="categories-heading" className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-[#F2F2F2]" style={{ fontFamily: "Chivo, sans-serif" }}>
              Categories
            </h1>
            <p className="text-[#A1A1A1] text-sm mt-2">Reorder, rename, or add new categories. Order shown here matches what shoppers see.</p>
          </div>
          {!showNew && (
            <Button onClick={() => setShowNew(true)} data-testid="new-category-btn" className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono text-xs uppercase tracking-wider">
              <Plus className="h-3 w-3 mr-2" /> New Category
            </Button>
          )}
        </div>

        {feedback && <div className="mb-6 p-3 border border-[#39FF14]/30 bg-[#39FF14]/5 text-[#39FF14] text-xs font-mono" data-testid="cat-feedback">{feedback}</div>}
        {error && <div className="mb-6 p-3 border border-red-500/30 bg-red-500/10 text-red-400 text-xs font-mono flex items-center justify-between" data-testid="cat-error"><span>{error}</span><button onClick={() => setError("")}><X className="h-3 w-3" /></button></div>}

        {/* Store-wide default shipping */}
        <div className="bg-[#0D0D0D] border border-[#1A1A1A] p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="default-shipping-panel">
          <div className="flex-1">
            <p className="font-mono uppercase tracking-[0.15em] text-[10px] font-bold text-[#39FF14] mb-1">Store-Wide Default Shipping</p>
            <p className="text-[#A1A1A1] text-xs">Applied to any category with no explicit rate set.</p>
          </div>
          {editingDefault ? (
            <div className="flex items-center gap-2">
              <Input
                type="number" min={0} step={0.01}
                value={defaultShippingDraft}
                onChange={(e) => setDefaultShippingDraft(e.target.value)}
                data-testid="default-shipping-input"
                className="bg-[#050505] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] font-mono w-28"
                autoFocus
              />
              <Button onClick={saveDefaultShipping} data-testid="default-shipping-save" className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono text-xs uppercase tracking-wider px-3">
                <Check className="h-3 w-3" />
              </Button>
              <Button onClick={() => { setEditingDefault(false); setDefaultShippingDraft(String(defaultShipping)); }} variant="outline" className="bg-transparent border-[#1A1A1A] text-[#A1A1A1] hover:text-[#39FF14] hover:border-[#39FF14] rounded-none font-mono text-xs px-3">
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <span className="font-mono text-2xl font-bold text-[#39FF14]" data-testid="default-shipping-value">${defaultShipping.toFixed(2)}</span>
              <Button onClick={() => setEditingDefault(true)} data-testid="default-shipping-edit" variant="outline" className="bg-transparent border-[#1A1A1A] text-[#A1A1A1] hover:text-[#39FF14] hover:border-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider">
                <Edit className="h-3 w-3 mr-1" /> Change
              </Button>
            </div>
          )}
        </div>

        {showNew && (
          <form onSubmit={handleCreate} className="bg-[#0D0D0D] border border-[#39FF14]/30 p-5 mb-6 space-y-4" data-testid="new-category-form">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-[#A1A1A1] font-mono text-[10px] uppercase tracking-wider">Key (URL slug)</Label>
                <Input
                  data-testid="new-cat-key"
                  required value={newKey}
                  onChange={(e) => setNewKey(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
                  placeholder="merch"
                  className="bg-[#050505] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] font-mono"
                />
                <p className="text-[#A1A1A1]/60 text-[10px] font-mono mt-1">Cannot be changed later</p>
              </div>
              <div>
                <Label className="text-[#A1A1A1] font-mono text-[10px] uppercase tracking-wider">Display Label</Label>
                <Input
                  data-testid="new-cat-label"
                  required value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="Merch"
                  className="bg-[#050505] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] font-mono"
                />
              </div>
              <div>
                <Label className="text-[#A1A1A1] font-mono text-[10px] uppercase tracking-wider">Shipping ($)</Label>
                <div className="flex items-center gap-2 mb-1">
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="new-cat-use-default">
                    <input
                      type="checkbox"
                      checked={newUseDefaultShipping}
                      onChange={(e) => setNewUseDefaultShipping(e.target.checked)}
                      className="accent-[#39FF14]"
                    />
                    <span className="font-mono text-[10px] text-[#A1A1A1]">Use default (${defaultShipping.toFixed(2)})</span>
                  </label>
                </div>
                <Input
                  data-testid="new-cat-shipping"
                  type="number" min={0} step={0.01}
                  disabled={newUseDefaultShipping}
                  value={newShipping}
                  onChange={(e) => setNewShipping(e.target.value)}
                  placeholder={newUseDefaultShipping ? `$${defaultShipping.toFixed(2)} (default)` : "Custom"}
                  className="bg-[#050505] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] font-mono disabled:opacity-50"
                />
              </div>
            </div>
            <div>
              <Label className="text-[#A1A1A1] font-mono text-[10px] uppercase tracking-wider">Description (optional)</Label>
              <textarea
                data-testid="new-cat-description"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="A short blurb shown to shoppers on the homepage card."
                rows={2}
                maxLength={300}
                className="w-full bg-[#050505] border border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] focus:outline-none focus:ring-1 focus:ring-[#39FF14] px-3 py-2 text-sm font-mono resize-none"
              />
              <p className="text-[#A1A1A1]/60 text-[10px] font-mono mt-1">{newDescription.length}/300</p>
            </div>
            <div className="flex gap-3">
              <Button type="submit" data-testid="new-cat-save" className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono text-xs uppercase tracking-wider px-6">
                Create
              </Button>
              <Button type="button" onClick={() => setShowNew(false)} variant="outline" className="bg-transparent border-[#1A1A1A] text-[#A1A1A1] hover:text-[#39FF14] hover:border-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider">
                Cancel
              </Button>
            </div>
          </form>
        )}

        <div className="bg-[#0D0D0D] border border-[#1A1A1A]" data-testid="categories-list">
          {categories.map((cat, idx) => (
            <div key={cat.key} data-testid={`category-${cat.key}`} className="px-4 py-3 border-b border-[#1A1A1A] last:border-b-0 flex flex-col sm:flex-row sm:items-center gap-3">
              {/* Reorder arrows */}
              <div className="flex sm:flex-col gap-1 shrink-0">
                <button onClick={() => reorder(cat.key, "up")} disabled={idx === 0} title="Move up" data-testid={`cat-up-${cat.key}`} className="w-7 h-7 flex items-center justify-center bg-[#050505] border border-[#1A1A1A] hover:border-[#39FF14] hover:text-[#39FF14] text-[#A1A1A1] disabled:opacity-30 disabled:cursor-not-allowed">
                  <ChevronUp className="h-3 w-3" />
                </button>
                <button onClick={() => reorder(cat.key, "down")} disabled={idx === categories.length - 1} title="Move down" data-testid={`cat-down-${cat.key}`} className="w-7 h-7 flex items-center justify-center bg-[#050505] border border-[#1A1A1A] hover:border-[#39FF14] hover:text-[#39FF14] text-[#A1A1A1] disabled:opacity-30 disabled:cursor-not-allowed">
                  <ChevronDown className="h-3 w-3" />
                </button>
              </div>

              {/* Image thumbnail + upload */}
              <div className="relative shrink-0 group" data-testid={`cat-image-${cat.key}`}>
                <div className="w-16 h-16 border border-[#1A1A1A] bg-[#050505] flex items-center justify-center overflow-hidden">
                  {cat.image_url ? (
                    <img src={cat.image_url} alt={cat.label} className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="h-5 w-5 text-[#1A1A1A]" />
                  )}
                  {uploadingKey === cat.key && (
                    <div className="absolute inset-0 bg-[#050505]/80 flex items-center justify-center">
                      <div className="w-4 h-4 border border-[#39FF14] border-t-transparent animate-spin" />
                    </div>
                  )}
                </div>
                <label
                  className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#39FF14] text-[#050505] flex items-center justify-center cursor-pointer hover:bg-[#4DFF2E] transition-colors"
                  title="Upload image"
                  data-testid={`cat-upload-${cat.key}`}
                >
                  <Upload className="h-3 w-3" strokeWidth={2.5} />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage(cat, f); e.target.value = ""; }}
                    className="hidden"
                    disabled={uploadingKey === cat.key}
                  />
                </label>
                {cat.image_url && (
                  <button
                    onClick={() => clearImage(cat)}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white flex items-center justify-center hover:bg-red-700 transition-colors opacity-0 group-hover:opacity-100"
                    title="Reset image"
                    data-testid={`cat-clear-image-${cat.key}`}
                  >
                    <X className="h-3 w-3" strokeWidth={2.5} />
                  </button>
                )}
              </div>

              {editing === cat.key ? (
                <div className="flex-1 flex flex-col gap-2">
                  <div className="flex flex-col sm:flex-row gap-2 items-stretch">
                    <Input
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      data-testid={`edit-label-${cat.key}`}
                      placeholder="Label"
                      className="bg-[#050505] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] font-mono flex-1"
                    />
                    <Input
                      type="number" min={0} step={0.01}
                      value={editShipping}
                      onChange={(e) => setEditShipping(e.target.value)}
                      data-testid={`edit-shipping-${cat.key}`}
                      placeholder={editUseDefaultShipping ? `$${defaultShipping.toFixed(2)} (default)` : "Shipping"}
                      disabled={editUseDefaultShipping}
                      className="bg-[#050505] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] font-mono w-full sm:w-32 disabled:opacity-50"
                    />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer" data-testid={`edit-use-default-${cat.key}`}>
                    <input
                      type="checkbox"
                      checked={editUseDefaultShipping}
                      onChange={(e) => setEditUseDefaultShipping(e.target.checked)}
                      className="accent-[#39FF14]"
                    />
                    <span className="font-mono text-[10px] text-[#A1A1A1]">Use default shipping (${defaultShipping.toFixed(2)})</span>
                  </label>
                  <textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    data-testid={`edit-description-${cat.key}`}
                    placeholder="Description (optional, shown on homepage card)"
                    rows={2}
                    maxLength={300}
                    className="w-full bg-[#050505] border border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] focus:outline-none focus:ring-1 focus:ring-[#39FF14] px-3 py-2 text-sm font-mono resize-none"
                  />
                  <div className="flex gap-2">
                    <Button onClick={() => saveEdit(cat)} data-testid={`save-${cat.key}`} className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono text-xs uppercase tracking-wider px-4">
                      <Check className="h-3 w-3 mr-1" /> Save
                    </Button>
                    <Button onClick={cancelEdit} variant="outline" className="bg-transparent border-[#1A1A1A] text-[#A1A1A1] hover:text-[#39FF14] hover:border-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider px-4">
                      Cancel
                    </Button>
                    <p className="font-mono text-[10px] text-[#A1A1A1]/60 ml-auto self-center">{editDescription.length}/300</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-[#F2F2F2]">{cat.label}</span>
                      <span className="font-mono text-[10px] text-[#A1A1A1] uppercase tracking-wider bg-[#1A1A1A] px-2 py-0.5">key: {cat.key}</span>
                      {cat.is_system && <span className="font-mono text-[10px] text-[#39FF14] uppercase tracking-wider bg-[#39FF14]/10 px-2 py-0.5">System</span>}
                    </div>
                    <p className="font-mono text-[10px] text-[#A1A1A1] mt-1">
                      Shipping {cat.shipping_cost == null
                        ? <span className="text-[#39FF14]">${defaultShipping.toFixed(2)} (default)</span>
                        : `$${cat.shipping_cost.toFixed(2)}`}
                      {" "}· order #{cat.sort_order}
                    </p>
                    {cat.description && <p className="text-xs text-[#A1A1A1] mt-1 line-clamp-2" data-testid={`cat-description-${cat.key}`}>{cat.description}</p>}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => startEdit(cat)} title="Edit label/shipping" data-testid={`edit-${cat.key}`} className="p-2 text-[#A1A1A1] hover:text-[#39FF14] transition-colors">
                      <Edit className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(cat)} title="Delete category" data-testid={`delete-${cat.key}`} className="p-2 text-[#A1A1A1] hover:text-red-400 transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        <p className="font-mono text-[10px] text-[#A1A1A1]/70 mt-4">
          Categories must be empty before deletion. Move or delete the products in them first. The &quot;Modded Consoles&quot; and &quot;Bundles&quot; categories trigger a $20 custom color upcharge based on their <code>key</code> — if you delete them and need that fee back, re-create with the same key (consoles / bundles).
        </p>
      </div>
    </div>
  );
}
